import { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { doc, updateDoc, deleteDoc, addDoc, collection, serverTimestamp, writeBatch } from 'firebase/firestore';

const SEED_REVIEWS = [
  { name: 'Ahmed Karimi',    role: 'CEO, TechStart Erbil',       rating: 5, text: 'AMKO completely transformed our brand. Everything was perfect.' },
  { name: 'Sara Rashid',     role: 'Marketing Director',          rating: 5, text: 'The event they managed for our product launch was spectacular.' },
  { name: 'Mohammad Hassan', role: 'Owner, Kurdistan Food Co.',   rating: 5, text: 'Our social media grew from 2K to 40K followers in 6 months.' },
];

const EMPTY_FORM = { name: '', role: '', rating: 5, text: '', approved: true };

function StarPicker({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {[1,2,3,4,5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
            fontSize: '1.3rem', color: n <= value ? '#F5A623' : '#d1d5db',
            transition: 'color 0.15s',
          }}
        >★</button>
      ))}
    </div>
  );
}

function ReviewForm({ initial = EMPTY_FORM, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  return (
    <div className="review-form-wrap">
      <div className="review-form-row">
        <div className="review-form-field">
          <label className="review-form-label">Client Name *</label>
          <input
            className="review-form-input"
            placeholder="e.g. Ahmed Al-Rashidi"
            value={form.name}
            onChange={set('name')}
            required
          />
        </div>
        <div className="review-form-field">
          <label className="review-form-label">Role / Company</label>
          <input
            className="review-form-input"
            placeholder="e.g. CEO at Startup"
            value={form.role}
            onChange={set('role')}
          />
        </div>
      </div>
      <div className="review-form-field">
        <label className="review-form-label">Rating</label>
        <StarPicker value={form.rating} onChange={v => setForm(p => ({ ...p, rating: v }))} />
      </div>
      <div className="review-form-field">
        <label className="review-form-label">Review Text *</label>
        <textarea
          className="review-form-input"
          rows={3}
          placeholder="What did the client say?"
          value={form.text}
          onChange={set('text')}
          style={{ resize: 'vertical' }}
        />
      </div>
      <div className="review-form-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <input
          type="checkbox"
          id="rev-approved"
          checked={form.approved}
          onChange={e => setForm(p => ({ ...p, approved: e.target.checked }))}
          style={{ width: 16, height: 16, cursor: 'pointer' }}
        />
        <label htmlFor="rev-approved" style={{ fontSize: '0.85rem', cursor: 'pointer' }}>
          Publish immediately (visible on website)
        </label>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button
          className="action-btn confirm"
          style={{ padding: '0.45rem 1.1rem' }}
          disabled={saving || !form.name.trim() || !form.text.trim()}
          onClick={() => onSave(form)}
        >
          {saving ? 'Saving…' : '✓ Save Review'}
        </button>
        <button className="action-btn delete" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default function AdminReviews({ reviews, setReviews, dataLoaded }) {
  const [adding, setAdding]     = useState(false);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [filter, setFilter]     = useState('all'); // all | pending | approved
  const seededRef = useRef(false);

  // Auto-seed default reviews into Firestore when collection is genuinely empty
  useEffect(() => {
    if (!dataLoaded || reviews.length > 0 || seededRef.current) return;
    seededRef.current = true;
    const seed = async () => {
      const batch = writeBatch(db);
      const seeded = SEED_REVIEWS.map(r => {
        const ref = doc(collection(db, 'reviews'));
        batch.set(ref, { ...r, approved: true, createdAt: serverTimestamp() });
        return { id: ref.id, ...r, approved: true, createdAt: new Date().toISOString() };
      });
      await batch.commit();
      setReviews(seeded);
    };
    seed();
  }, [dataLoaded, reviews.length, setReviews]);

  const filtered = reviews.filter(r => {
    if (filter === 'pending')  return !r.approved;
    if (filter === 'approved') return r.approved;
    return true;
  });

  const pendingCount  = reviews.filter(r => !r.approved).length;
  const approvedCount = reviews.filter(r =>  r.approved).length;

  const handleAdd = async form => {
    setSaving(true);
    try {
      const ref = await addDoc(collection(db, 'reviews'), {
        name: form.name.trim(),
        role: form.role.trim(),
        rating: form.rating,
        text: form.text.trim(),
        approved: form.approved,
        createdAt: serverTimestamp(),
      });
      setReviews(p => [{
        id: ref.id,
        name: form.name.trim(),
        role: form.role.trim(),
        rating: form.rating,
        text: form.text.trim(),
        approved: form.approved,
        createdAt: new Date().toISOString(),
      }, ...p]);
      setAdding(false);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (id, form) => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'reviews', id), {
        name:     form.name.trim(),
        role:     form.role.trim(),
        rating:   form.rating,
        text:     form.text.trim(),
        approved: form.approved,
      });
      setReviews(p => p.map(r => r.id === id
        ? { ...r, name: form.name.trim(), role: form.role.trim(), rating: form.rating, text: form.text.trim(), approved: form.approved }
        : r
      ));
      setEditId(null);
    } finally {
      setSaving(false);
    }
  };

  const toggleApprove = async (id, approved) => {
    await updateDoc(doc(db, 'reviews', id), { approved });
    setReviews(p => p.map(r => r.id === id ? { ...r, approved } : r));
  };

  const remove = async id => {
    if (!window.confirm('Delete this review permanently?')) return;
    await deleteDoc(doc(db, 'reviews', id));
    setReviews(p => p.filter(r => r.id !== id));
  };

  return (
    <div>
      {/* Header */}
      <div className="reviews-admin-header">
        <div>
          <h1 style={{ marginBottom: 4 }}>
            Reviews
            {pendingCount > 0 && <span className="count" style={{ marginLeft: 8 }}>{pendingCount} pending</span>}
          </h1>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: 0 }}>
            {reviews.length} total &nbsp;·&nbsp; {approvedCount} published &nbsp;·&nbsp; {pendingCount} awaiting approval
          </p>
        </div>
        <button
          className="action-btn confirm"
          style={{ padding: '0.5rem 1.2rem', fontSize: '0.85rem' }}
          onClick={() => { setAdding(a => !a); setEditId(null); }}
        >
          {adding ? '✕ Cancel' : '+ Add Review'}
        </button>
      </div>

      {/* Add form */}
      {adding && (
        <div className="admin-card" style={{ marginBottom: '1.25rem', borderColor: '#86efac' }}>
          <p style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: '1rem', fontSize: '0.9rem' }}>
            ➕ Add New Review
          </p>
          <ReviewForm
            onSave={handleAdd}
            onCancel={() => setAdding(false)}
            saving={saving}
          />
        </div>
      )}

      {/* Filter tabs */}
      <div className="reviews-filter-tabs">
        {[
          { key: 'all',      label: `All (${reviews.length})` },
          { key: 'pending',  label: `Pending (${pendingCount})` },
          { key: 'approved', label: `Published (${approvedCount})` },
        ].map(t => (
          <button
            key={t.key}
            className={`reviews-tab${filter === t.key ? ' active' : ''}`}
            onClick={() => setFilter(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Review list */}
      {filtered.length === 0 ? (
        <div className="empty">
          {filter === 'pending'  ? 'No pending reviews.' :
           filter === 'approved' ? 'No published reviews yet.' :
           'No reviews yet. Add one above or wait for clients to submit.'}
        </div>
      ) : (
        filtered.map(r => (
          <div key={r.id} className={`admin-card${editId === r.id ? ' editing' : ''}`} style={{ marginBottom: '0.75rem' }}>
            {editId === r.id ? (
              <>
                <p style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: '1rem', fontSize: '0.9rem' }}>
                  ✏️ Editing — {r.name}
                </p>
                <ReviewForm
                  initial={{ name: r.name, role: r.role || '', rating: r.rating || 5, text: r.text, approved: r.approved }}
                  onSave={form => handleEdit(r.id, form)}
                  onCancel={() => setEditId(null)}
                  saving={saving}
                />
              </>
            ) : (
              <>
                <div className="card-header">
                  <strong>{r.name}</strong>
                  {r.role && <span className="meta">{r.role}</span>}
                  <span style={{ color: '#F5A623', fontSize: '0.9rem', letterSpacing: 1 }}>
                    {'★'.repeat(r.rating || 5)}{'☆'.repeat(5 - (r.rating || 5))}
                  </span>
                  <span className={`badge ${r.approved ? 'badge-confirmed' : 'badge-pending'}`}>
                    {r.approved ? '✓ Published' : '⏳ Pending'}
                  </span>
                  {r.createdAt && (
                    <span className="meta" style={{ marginLeft: 'auto' }}>
                      {new Date(r.createdAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <p className="card-notes" style={{ marginBottom: '0.85rem' }}>"{r.text}"</p>
                <div className="card-actions">
                  <button className="action-btn edit" onClick={() => { setEditId(r.id); setAdding(false); }}>✏ Edit</button>
                  {r.approved
                    ? <button className="action-btn cancel" onClick={() => toggleApprove(r.id, false)}>✕ Unpublish</button>
                    : <button className="action-btn confirm" onClick={() => toggleApprove(r.id, true)}>✓ Publish</button>
                  }
                  <button className="action-btn delete" onClick={() => remove(r.id)}>🗑 Delete</button>
                </div>
              </>
            )}
          </div>
        ))
      )}
    </div>
  );
}
